export const COUPLE_PHOTO_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif";

export const COUPLE_PHOTO_MAX_INPUT_BYTES = 25 * 1024 * 1024;
export const COUPLE_PHOTO_DOWNSCALE_BYTES = 4 * 1024 * 1024;
export const COUPLE_PHOTO_MAX_EDGE = 2000;

export const COUPLE_PHOTO_ACCEPTED_LABEL =
  "Accepted formats: JPEG, PNG, WebP, GIF, and HEIC/HEIF. Maximum 25 MB — photos over 4 MB or 2000px on the long side are resized to JPEG before upload.";

const ALLOWED_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
  "heic",
  "heif",
]);

const ALLOWED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
]);

const HEIC_CONVERT_ERROR =
  "Couldn’t convert this HEIC photo to JPEG. Export it as JPEG from your phone Photos app, then upload again.";

export function isHeicLike(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type === "image/heic" ||
    type === "image/heif" ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

export function isAllowedCouplePhoto(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  const ext = file.name.includes(".")
    ? file.name.split(".").pop()!.toLowerCase()
    : "";

  if (type && ALLOWED_MIME_TYPES.has(type)) return true;
  if (ext && ALLOWED_EXTENSIONS.has(ext)) return true;
  return false;
}

export type PreparedCouplePhoto = {
  file: File;
  convertedFromHeic: boolean;
};

type DrawableImage = {
  width: number;
  height: number;
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
  close: () => void;
};

function jpegNameFrom(name: string): string {
  const replaced = name.replace(/\.(heic|heif)$/i, ".jpg");
  const base = replaced.replace(/\.[^.]+$/, "") || "couple-photo";
  return `${base}.jpg`;
}

async function blobToImage(blob: Blob): Promise<DrawableImage> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(blob);
    return {
      width: bitmap.width,
      height: bitmap.height,
      draw: (ctx, w, h) => ctx.drawImage(bitmap, 0, 0, w, h),
      close: () => bitmap.close(),
    };
  }

  const url = URL.createObjectURL(blob);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () =>
      reject(
        new Error("Couldn’t read this image. Try a JPEG or PNG instead.")
      );
    el.src = url;
  });
  return {
    width: img.naturalWidth,
    height: img.naturalHeight,
    draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
    close: () => URL.revokeObjectURL(url),
  };
}

async function canvasToJpegFile(
  canvas: HTMLCanvasElement,
  name: string,
  quality: number
): Promise<File> {
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) =>
        b
          ? resolve(b)
          : reject(
              new Error(
                "Couldn’t resize this photo. Export it as JPEG from your phone Photos app and try again."
              )
            ),
      "image/jpeg",
      quality
    );
  });
  return new File([blob], jpegNameFrom(name), { type: "image/jpeg" });
}

async function drawableToJpegFile(
  source: DrawableImage,
  name: string,
  quality: number
): Promise<File> {
  const maxDim = Math.max(source.width, source.height) || 1;
  const scale = maxDim > COUPLE_PHOTO_MAX_EDGE ? COUPLE_PHOTO_MAX_EDGE / maxDim : 1;
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error(
      "Couldn’t resize this photo. Export it as JPEG from your phone Photos app and try again."
    );
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  source.draw(ctx, width, height);
  return await canvasToJpegFile(canvas, name, quality);
}

async function downscaleIfNeeded(file: File): Promise<File> {
  let source: DrawableImage;
  try {
    source = await blobToImage(file);
  } catch {
    throw new Error("Couldn’t read this image. Try a JPEG or PNG instead.");
  }

  try {
    const maxDim = Math.max(source.width, source.height) || 1;
    const needsResize =
      maxDim > COUPLE_PHOTO_MAX_EDGE || file.size > COUPLE_PHOTO_DOWNSCALE_BYTES;
    if (!needsResize) return file;
    return await drawableToJpegFile(source, file.name, 0.88);
  } finally {
    source.close();
  }
}

async function tryNativeHeicToJpeg(file: File): Promise<File | null> {
  try {
    const source = await blobToImage(file);
    try {
      if (source.width < 2 || source.height < 2) return null;
      return await drawableToJpegFile(source, file.name, 0.92);
    } finally {
      source.close();
    }
  } catch {
    return null;
  }
}

async function convertHeicWithLibheif(file: File): Promise<File> {
  const { heicTo } = await import("heic-to");

  try {
    const bitmap = await heicTo({ blob: file, type: "bitmap" });
    const source: DrawableImage = {
      width: bitmap.width,
      height: bitmap.height,
      draw: (ctx, w, h) => ctx.drawImage(bitmap, 0, 0, w, h),
      close: () => bitmap.close(),
    };
    try {
      if (source.width < 2 || source.height < 2) {
        throw new Error("Decoded HEIC had no pixels");
      }
      return await drawableToJpegFile(source, file.name, 0.92);
    } finally {
      source.close();
    }
  } catch (bitmapErr) {
    const blob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.92,
    });
    if (!blob || blob.size < 32) {
      throw bitmapErr;
    }
    return new File([blob], jpegNameFrom(file.name), { type: "image/jpeg" });
  }
}

async function convertHeicToJpeg(file: File): Promise<File> {
  const native = await tryNativeHeicToJpeg(file);
  if (native) return native;

  try {
    return await convertHeicWithLibheif(file);
  } catch (err) {
    console.warn("HEIC conversion failed", err);
    throw new Error(HEIC_CONVERT_ERROR);
  }
}

/**
 * Validate, convert HEIC/HEIF to JPEG, then downscale oversized images for upload.
 */
export async function prepareCouplePhoto(file: File): Promise<PreparedCouplePhoto> {
  if (!isAllowedCouplePhoto(file)) {
    throw new Error(
      "That file type isn’t supported. Please use JPEG, PNG, WebP, GIF, or HEIC."
    );
  }

  if (file.size > COUPLE_PHOTO_MAX_INPUT_BYTES) {
    throw new Error(
      "This photo is too large. Please use a file under 25 MB, or export a smaller JPEG from your phone and try again."
    );
  }

  let working = file;
  let convertedFromHeic = false;

  if (isHeicLike(file)) {
    working = await convertHeicToJpeg(file);
    convertedFromHeic = true;
  }

  const resized = await downscaleIfNeeded(working);
  return { file: resized, convertedFromHeic };
}

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
